'use client';

import { cn } from '@/shared/lib/utils';
import { Check, CheckCheck } from 'lucide-react';

type MessageBubbleProps = {
  role: 'user' | 'assistant' | string;
  text: string;
  timestamp?: string;
  status?: 'sent' | 'delivered' | 'read';
};

export function MessageBubble({ role, text, timestamp, status = 'read' }: MessageBubbleProps) {
  const isUser = role === 'user';

  // Use current time as default timestamp
  const displayTimestamp =
    timestamp ||
    new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  return (
    <div className={cn('mb-2 flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div className='relative max-w-[75%]'>
        <div
          className={cn(
            'relative rounded-2xl px-4 py-2.5 shadow-sm transition-all duration-200',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary text-secondary-foreground border border-border/40',
          )}
        >
          <p className='text-[15px] leading-relaxed whitespace-pre-wrap break-keep'>
            {text || <span className='opacity-70'>...</span>}
          </p>
          <div
            className={cn(
              'mt-1 flex items-center justify-end gap-1',
              isUser ? 'text-primary-foreground/70' : 'text-muted-foreground',
            )}
          >
            <span className='text-[11px]'>{displayTimestamp}</span>
            {isUser && (
              <span className='ml-0.5'>
                {status === 'read' ? (
                  <CheckCheck className='h-3.5 w-3.5' />
                ) : (
                  <Check className='h-3.5 w-3.5' />
                )}
              </span>
            )}
          </div>
          {/* Bubble tail - SVG (k-doc style) */}
          {isUser ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="19"
              viewBox="0 0 12 19"
              fill="none"
              className="pointer-events-none absolute right-[-5px] bottom-0 z-0"
              aria-hidden
            >
              <path
                d="M11.8886 18.8631C6.73688 19.6573 1.6512 16.8201 0 15L7 0V6C7 17.5 12.8793 18.2642 11.8886 18.8631Z"
                style={{ fill: 'var(--primary)' }}
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="19"
              viewBox="0 0 12 19"
              fill="none"
              className="pointer-events-none absolute top-0 left-[-5px] z-0"
              aria-hidden
            >
              <path
                d="M0.111382 0.137075C5.26312 -0.657162 10.3488 2.18006 12 4.00018L5 19.0002V13.0002C5 1.50018 -0.879335 0.736019 0.111382 0.137075Z"
                style={{ fill: 'var(--secondary)' }}
              />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}
